/**
 *
 * @module submitOrderWithPaymentMethodRequest
 *
 */

import Check from 'check-types';
import Services from './../services/entry';
import LineItem from './lineItem';

const RedemptionRequest = Services.Commerce.RedemptionRequest;
const OrderCampaign = Services.Commerce.OrderCampaign;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    nonEmptyString,
    nonEmptyObject,
    instanceStrict
} = Check.assert;

/**
 *
 * @class SubmitOrderWithPaymentMethodRequest
 * @since 3.5.0
 * @desc Information necessary to place an order.
 *
 */
export default class SubmitOrderWithPaymentMethodRequest {

    /**
     *
     * @param {Object} options
     * @param {Array<SDK.Commerce.LineItem>} options.lineItems
     * @param {String} options.paymentMethodId
     * @param {Array<SDK.Services.Commerce.OrderCampaign>} options.orderCampaigns
     * @param {String} options.emailAddress
     * @param {SDK.Services.Commerce.RedemptionRequest} [options.redemptionRequest=undefined]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'SubmitOrderWithPaymentMethodRequest(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get lineItems() {
                return `${this.ctor} options.lineItems should be an Array of SDK.Commerce.LineItem`;
            },
            get orderCampaigns() {
                return `${this.ctor} options.orderCampaigns should be an Array of SDK.Services.Commerce.OrderCampaign`;
            },
            get paymentMethodId() {
                return `${this.ctor} options.paymentMethodId should be a String with length`;
            },
            get emailAddress() {
                return `${this.ctor} options.emailAddress should be a String with length`;
            },
            get redemptionRequest() {
                return `${this.ctor} options.redemptionRequest should be an instance of SDK.Services.Commerce.RedemptionRequest`; //eslint-disable-line
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { lineItems, orderCampaigns, paymentMethodId, emailAddress, redemptionRequest } = options;

        array.of.instanceStrict(lineItems, LineItem, errorMessage.lineItems, InvalidArgumentException);
        array.of.instanceStrict(orderCampaigns, OrderCampaign, errorMessage.orderCampaigns, InvalidArgumentException);
        nonEmptyString(paymentMethodId, errorMessage.paymentMethodId, InvalidArgumentException);
        nonEmptyString(emailAddress, errorMessage.emailAddress, InvalidArgumentException);

        if (Check.assigned(redemptionRequest)) {
            instanceStrict(
                redemptionRequest,
                RedemptionRequest,
                errorMessage.redemptionRequest,
                InvalidArgumentException
            );
        }

        /**
         *
         * @access public
         * @type {Array<SDK.Commerce.LineItems>}
         * @desc Products to be purchased in this order.
         *
         */
        this.lineItems = lineItems;

        /**
         *
         * @access public
         * @type {String}
         * @desc Payment method ID retrieved from a request to create or get a payment method.
         *
         */
        this.paymentMethodId = paymentMethodId;

        /**
         *
         * @access public
         * @type {Array<SDK.Services.Commerce.OrderCampaign>}
         * @desc Information relevant to special pricing considerations for an order.
         *
         */
        this.orderCampaigns = orderCampaigns;

        /**
         *
         * @access public
         * @type {String}
         * @desc Email address of the user.
         *
         */
        this.emailAddress = emailAddress;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.RedemptionRequest}
         * @desc The account ID or SWID for the current user.
         *
         */
        this.redemptionRequest = redemptionRequest;

    }

}



// WEBPACK FOOTER //
// ./src/commerce/submitOrderWithPaymentMethodRequest.js